import type { Bounds, Vec2 } from '@/types';
import { containsBounds, containsPoint, intersects } from '@/core/geometry/bounds';

export interface QuadTreeItem<T> {
  readonly id: T;
  readonly bounds: Bounds;
}

interface Node<T> {
  bounds: Bounds;
  items: QuadTreeItem<T>[];
  children: [Node<T>, Node<T>, Node<T>, Node<T>] | null;
  depth: number;
}

export interface QuadTreeOptions {
  /** Items per node before splitting. */
  readonly capacity: number;
  readonly maxDepth: number;
}

const DEFAULT_OPTIONS: QuadTreeOptions = { capacity: 8, maxDepth: 12 };

/**
 * A loose quadtree for axis-aligned bounds. Items that straddle a split line stay
 * in the parent node, so every item lives in exactly one node and removal is O(log n)
 * via the id → node index.
 *
 * The tree grows outward when an item is inserted outside the root: the world is
 * unbounded, so the root doubles in size (re-rooting) until the item fits.
 */
export class QuadTree<T> {
  private root: Node<T>;
  private readonly index = new Map<T, Node<T>>();
  private readonly options: QuadTreeOptions;

  constructor(initialBounds: Bounds, options: Partial<QuadTreeOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.root = { bounds: initialBounds, items: [], children: null, depth: 0 };
  }

  get size(): number {
    return this.index.size;
  }

  /** Structural counts for diagnostics: nodes, leaves, deepest level and the root extent. */
  stats(): { readonly nodes: number; readonly leaves: number; readonly maxDepth: number; readonly bounds: Bounds } {
    let nodes = 0;
    let leaves = 0;
    let maxDepth = 0;
    const walk = (node: Node<T>): void => {
      nodes++;
      maxDepth = Math.max(maxDepth, node.depth);
      if (node.children) for (const c of node.children) walk(c);
      else leaves++;
    };
    walk(this.root);
    return { nodes, leaves, maxDepth, bounds: this.root.bounds };
  }

  insert(item: QuadTreeItem<T>): void {
    if (this.index.has(item.id)) this.remove(item.id);
    while (!containsBounds(this.root.bounds, item.bounds)) this.grow(item.bounds);
    this.insertInto(this.root, item);
  }

  update(item: QuadTreeItem<T>): void {
    const node = this.index.get(item.id);
    // Fast path: still fits its current node and the node has no children to descend into.
    if (node?.children === null && containsBounds(node.bounds, item.bounds)) {
      const i = node.items.findIndex((x) => x.id === item.id);
      if (i !== -1) {
        node.items[i] = item;
        return;
      }
    }
    this.insert(item);
  }

  remove(id: T): boolean {
    const node = this.index.get(id);
    if (!node) return false;
    const i = node.items.findIndex((x) => x.id === id);
    if (i !== -1) node.items.splice(i, 1);
    this.index.delete(id);
    return true;
  }

  clear(): void {
    this.root = { bounds: this.root.bounds, items: [], children: null, depth: 0 };
    this.index.clear();
  }

  /** All items whose bounds intersect the query rectangle. */
  query(area: Bounds): T[] {
    const out: T[] = [];
    this.collect(this.root, area, out);
    return out;
  }

  /** All items whose bounds contain the point. */
  queryPoint(point: Vec2): T[] {
    const out: T[] = [];
    this.collectPoint(this.root, point, out);
    return out;
  }

  private collect(node: Node<T>, area: Bounds, out: T[]): void {
    if (!intersects(node.bounds, area)) return;
    for (const item of node.items) if (intersects(item.bounds, area)) out.push(item.id);
    if (node.children) for (const child of node.children) this.collect(child, area, out);
  }

  private collectPoint(node: Node<T>, point: Vec2, out: T[]): void {
    if (!containsPoint(node.bounds, point)) return;
    for (const item of node.items) if (containsPoint(item.bounds, point)) out.push(item.id);
    if (node.children) for (const child of node.children) this.collectPoint(child, point, out);
  }

  private insertInto(node: Node<T>, item: QuadTreeItem<T>): void {
    if (node.children) {
      const child = node.children.find((c) => containsBounds(c.bounds, item.bounds));
      if (child) {
        this.insertInto(child, item);
        return;
      }
    }
    node.items.push(item);
    this.index.set(item.id, node);
    if (node.children === null && node.items.length > this.options.capacity && node.depth < this.options.maxDepth) {
      this.split(node);
    }
  }

  private split(node: Node<T>): void {
    const { minX, minY, maxX, maxY } = node.bounds;
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    const mk = (b: Bounds): Node<T> => ({ bounds: b, items: [], children: null, depth: node.depth + 1 });
    node.children = [
      mk({ minX, minY, maxX: midX, maxY: midY }),
      mk({ minX: midX, minY, maxX, maxY: midY }),
      mk({ minX, minY: midY, maxX: midX, maxY }),
      mk({ minX: midX, minY: midY, maxX, maxY }),
    ];
    const staying: QuadTreeItem<T>[] = [];
    for (const item of node.items) {
      const child = node.children.find((c) => containsBounds(c.bounds, item.bounds));
      if (child) {
        child.items.push(item);
        this.index.set(item.id, child);
      } else {
        staying.push(item);
      }
    }
    node.items = staying;
  }

  /** Doubles the root in the direction of `target` and re-parents the old root. */
  private grow(target: Bounds): void {
    const b = this.root.bounds;
    const w = b.maxX - b.minX;
    const h = b.maxY - b.minY;
    const growLeft = target.minX < b.minX;
    const growUp = target.minY < b.minY;
    const newBounds: Bounds = {
      minX: growLeft ? b.minX - w : b.minX,
      minY: growUp ? b.minY - h : b.minY,
      maxX: growLeft ? b.maxX : b.maxX + w,
      maxY: growUp ? b.maxY : b.maxY + h,
    };
    const oldRoot = this.root;
    this.root = { bounds: newBounds, items: [], children: null, depth: 0 };
    // Re-insert everything; depth bookkeeping is reset so the tree stays balanced.
    const all = this.allItems(oldRoot);
    this.index.clear();
    for (const item of all) this.insertInto(this.root, item);
  }

  private allItems(node: Node<T>): QuadTreeItem<T>[] {
    const out = [...node.items];
    if (node.children) for (const c of node.children) out.push(...this.allItems(c));
    return out;
  }
}
